import { Notification, NotificationTheme } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import {
  logChatbotStatusBannerStatusBannerClicked,
  logChatbotStatusBannerStatusBannerDismissed,
  logChatbotStatusBannerStatusBannerShowed,
} from '../../../bi';
import { headerActions } from '../../reducers/header';
import { hideStatusBannerData, selectStatusBannerData } from '../../selectors/header';
import s from './StatusBanner.scss';

const AUTO_HIDE_TIMEOUT = 10 * 1000; // 10 seconds

type StatusBannerProps = {
  autoHide?: boolean;
};

export const StatusBanner: FC<StatusBannerProps> = ({ autoHide }) => {
  const store = useStore();
  const dispatch = useDispatch();
  const statusBannerData = useSelector(selectStatusBannerData);
  const hideStatusBanner = useSelector(hideStatusBannerData);
  const { t } = useTranslation<TLocalesKeys>();

  const onStatusBannerClick = useCallback(
    (e) => {
      if (e.target?.tagName === 'A') {
        const state = store.getState();
        logChatbotStatusBannerStatusBannerClicked(state, statusBannerData!.biData);
      }
    },
    [store, statusBannerData],
  );

  const onStatusBannerClose = useCallback(() => {
    if (statusBannerData) {
      const state = store.getState();
      logChatbotStatusBannerStatusBannerDismissed(state, statusBannerData?.biData);
      dispatch(headerActions.resetStatusBanner());
    }
  }, [dispatch, statusBannerData]);

  useEffect(() => {
    if (statusBannerData) {
      const state = store.getState();
      logChatbotStatusBannerStatusBannerShowed(state, statusBannerData.biData);
    }
  }, [statusBannerData]);

  return (
    <div className={s.statusBannerWrapper}>
      <Notification
        dataHook="status-banner"
        show={!!statusBannerData && !hideStatusBanner}
        autoHideTimeout={autoHide ? AUTO_HIDE_TIMEOUT : undefined}
        theme={statusBannerData?.theme as NotificationTheme}
        onClose={onStatusBannerClose}
      >
        <Notification.TextLabel ellipsis={false} textAlign="start">
          <span
            className={s.statusBannerContent}
            onClick={onStatusBannerClick}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t(`status-banner.${statusBannerData?.textKey}`, {
                  name: statusBannerData?.incidentName,
                }),
              ),
            }}
          />
        </Notification.TextLabel>
        <Notification.CloseButton />
      </Notification>
    </div>
  );
};
